'use client';

import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';

export interface ColorSwatchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  /** Any CSS colour. `null` renders the transparency checkerboard ("no colour"). */
  color: string | null;
  selected?: boolean;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'square' | 'circle';
  /** Accessible name; defaults to the colour value. */
  label?: string;
  /** Render as a non-interactive chip. */
  readOnly?: boolean;
}

const SWATCH_SIZE = { sm: 'size-6', md: 'size-8', lg: 'size-11' } as const;

/**
 * One colour cell. Interactive by default (used inside ColorPicker and the
 * theme palette row); `readOnly` turns it into a plain chip for docs and legends.
 */
export const ColorSwatch = React.forwardRef<HTMLButtonElement, ColorSwatchProps>(function ColorSwatch(
  { color, selected, size = 'md', shape = 'square', label, readOnly, className, ...props },
  ref,
) {
  const common = cn(
    'relative inline-flex shrink-0 items-center justify-center border border-line-strong',
    'shadow-[inset_0_0_0_1px_rgb(255_255_255/0.35)]',
    shape === 'circle' ? 'rounded-full' : 'rounded-md',
    SWATCH_SIZE[size],
    color === null && 'k-checker',
    selected && 'ring-2 ring-accent ring-offset-2 ring-offset-surface',
    className,
  );
  const style = color ? { backgroundColor: color } : undefined;
  const name = label ?? (color ?? 'ללא צבע');

  if (readOnly) {
    return <span className={common} style={style} title={name} aria-label={name} role="img" />;
  }

  return (
    <button
      ref={ref}
      type="button"
      role="radio"
      aria-checked={Boolean(selected)}
      aria-label={name}
      title={name}
      className={cn(common, 'k-hit cursor-pointer transition-transform hover:scale-105 active:scale-100')}
      style={style}
      {...props}
    >
      {selected ? (
        <Check
          className="size-4 drop-shadow-[0_1px_1px_rgb(0_0_0/0.45)]"
          style={{ color: isLight(color) ? '#1b1b1f' : '#ffffff' }}
          aria-hidden
        />
      ) : null}
      {color === null ? (
        <span className="absolute inset-0 rounded-[inherit] bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),var(--color-danger)_50%,transparent_calc(50%+1px))]" />
      ) : null}
    </button>
  );
});

/** Rough perceived lightness so the check mark stays readable on any swatch. */
export function isLight(color: string | null): boolean {
  if (!color) return true;
  const hex = color.trim().replace('#', '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (full.length < 6) return true;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return true;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
}
